"use client";

import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "@/lib/motion";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

type ModalShellProps = {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  size?: "md" | "lg" | "xl" | "video";
  children: React.ReactNode;
};

const SIZE_CLASS = {
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
  video: "max-w-4xl",
} as const;

export default function ModalShell({
  open,
  onClose,
  ariaLabel,
  size = "lg",
  children,
}: ModalShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key === "Tab") {
        const root = contentRef.current;
        if (!root) return;
        const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    // Move focus into the dialog so keyboard users start inside it.
    const focusTimer = window.setTimeout(() => {
      const root = contentRef.current;
      (root?.querySelector<HTMLElement>(FOCUSABLE) ?? root)?.focus();
    }, 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(focusTimer);
      // Restore focus to whatever opened the modal.
      previouslyFocused?.focus?.();
    };
    // onClose is read via onCloseRef so a new closure each render doesn't retrigger this
    // effect — that used to steal focus back out of the dialog's inputs on every keystroke.
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="modal-shell"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6"
          style={{
            background: "rgba(0,0,0,0.78)",
            backdropFilter: "blur(6px)",
          }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            ref={contentRef}
            tabIndex={-1}
            className={`relative my-auto w-full ${SIZE_CLASS[size]} outline-none`}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
